import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import authAPI from "@/api/auth";
import tenantsAPI from "@/api/tenants";
import { normalizeData, normalizeIncluded, normalizeResource } from "@/utils/jsonapi";
import { readRememberedTenantId } from "@/utils/tenantMemory";
import {
  classifyProvisioningError,
  OUTCOME_FAILED,
  SIGNUP_STATUS_COMPLETED,
  SIGNUP_STATUS_PROVISIONING,
} from "./signupProvisioning";
import {
  impersonatedTenantEntity,
  normalizeImpersonation,
  selectImpersonation,
} from "./impersonation";
import { IMPERSONATION_EXITED } from "./impersonationActionTypes";
import { isPublicPath, markSessionExpired } from "./sessionExpiry";
import { selectIsHostTenantPending } from "./hostTenantSlice";

/** @typedef {import('@/types/models/user').User} User */
/** @typedef {import('@/types/models/tenant').Tenant} Tenant */

/**
 * Entity adapter for tenants (normalized state)
 */
const tenantsAdapter = createEntityAdapter({
  selectId: (tenant) => tenant.id,
  sortComparer: (a, b) => (a.name || '').localeCompare(b.name || ''),
});

/**
 * Entity adapter for tenant memberships (normalized state)
 */
const tenantMembershipsAdapter = createEntityAdapter({
  selectId: (membership) => membership.id,
});

/**
 * Fetch current user and their tenants
 * Uses JSON:API ?include=all to get user with tenant_memberships and tenants in one request
 */
export const fetchCurrentUser = createAsyncThunk(
  "auth/fetchCurrentUser",
  async (_, { rejectWithValue, getState }) => {
    try {
      // Fetch user with ?include=all to get tenant_memberships and tenants
      const userResponse = await authAPI.getCurrentUser();

      // Normalize user data from JSON:API format
      // @ts-ignore - API returns Object but has data property
      const user = normalizeData(userResponse.data);

      // NOTE: We don't call processIncluded() here to avoid circular dependency
      // The tenant and tenant_membership data will be extracted manually below
      // and stored directly in the auth slice

      // Extract tenants and tenant_memberships from included section
      let tenants = [];
      let tenantMemberships = [];
      // @ts-ignore - API returns Object but has included property
      if (userResponse.included && Array.isArray(userResponse.included)) {
        const included = normalizeIncluded(userResponse.included);

        // Get tenants from the normalized included map
        if (included.tenant) {
          tenants = Object.values(included.tenant);
        }

        // Get tenant_memberships from the normalized included map
        if (included.tenant_membership) {
          tenantMemberships = Object.values(included.tenant_membership);
        }
      }

      // Emergency fallback: Extract tenant directly from raw included array
      if (tenants.length === 0) {
        console.warn('[fetchCurrentUser] No tenants extracted from normalizeIncluded');

        // Try extracting tenants directly from the raw included array as last resort
        // @ts-ignore
        if (userResponse.included && Array.isArray(userResponse.included)) {
          // @ts-ignore
          const tenantResources = userResponse.included.filter((r) => r.type === 'tenant');
          if (tenantResources.length > 0) {
            // @ts-ignore
            tenants = tenantResources.map((r) => normalizeResource(r));
          }
        }
      }

      return {
        user,
        tenants,
        tenantMemberships,
        // The tenant this user last had active (see fetchCurrentUser.fulfilled).
        // Read here in the thunk, not the reducer, so the reducer stays pure.
        // It is validated against the membership list before it is trusted.
        rememberedTenantId: readRememberedTenantId(),
        // A superuser impersonating from Django admin (ClickUp 86e39gctg).
        // Read from the user resource's attributes; `meta` is accepted too
        // so the app doesn't depend on which one the backend chose.
        // @ts-ignore - API returns Object but has meta property
        impersonation: normalizeImpersonation(user?.impersonation ?? userResponse.meta?.impersonation),
      };
    } catch (error) {
      // A session rehydrated from storage (isAuthenticated is persisted, the
      // verification is not) that /auth/me now refuses has expired: say so on
      // the login page. ProtectedRoute makes the one, in-app redirect once the
      // rejection clears the auth state; the API client leaves the shell's
      // own 401s from this window alone (api/client.js). A public page (a
      // share, embed, invitation, unsubscribe, verification or reset link)
      // never flags it: nobody there is being signed out.
      const { isAuthenticated, isSessionVerified } = getState().auth || {};
      const isStaleSession = error.apiError?.status === 401 && isAuthenticated && !isSessionVerified;
      if (isStaleSession && !isPublicPath(window.location.pathname)) markSessionExpired();
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Refresh /auth/me and make sure a tenant is selected before routing to
 * Home - used right after onboarding finishes (signup's last step). Without
 * this, a session that leaves onboarding before its tenant is "current"
 * lands on Home with nothing selected: the dashboards list spins on a
 * skeleton and the header shows "No tenant selected" forever (onboarding
 * audit Critical #2). fetchCurrentUser already auto-selects a remembered or
 * single tenant; this only steps in when that still leaves nothing chosen
 * (no remembered tenant and more than one membership) by falling back to
 * the first membership - Home has no tenant picker to ask the user instead.
 */
export const refreshSessionTenantContext = createAsyncThunk(
  "auth/refreshSessionTenantContext",
  async (_, { dispatch, getState }) => {
    await dispatch(fetchCurrentUser());
    if (!selectCurrentTenantId(getState())) {
      const firstTenantId = selectTenants(getState())[0]?.id;
      if (firstTenantId) {
        dispatch(setTenant(firstTenantId));
      }
    }
  }
);

/**
 * Whether the login page may offer "use password instead" (ClickUp
 * 86e3ay4ec). The backend's `password_login_available` is authoritative;
 * an older backend without it falls back to the same rule: always when no
 * workspace resolved (bare app domain - the password endpoint applies the
 * user's own policy), otherwise the workspace's password method.
 * @param {Object} attributes - login-context attributes
 * @returns {boolean}
 */
function passwordLoginAvailableFrom(attributes) {
  if (typeof attributes.password_login_available === "boolean") {
    return attributes.password_login_available;
  }
  if (!attributes.tenant_resolved) return true;
  return (Array.isArray(attributes.methods) ? attributes.methods : []).some(
    (method) => method.type === "password" && method.enabled
  );
}

/**
 * Fetch tenant-aware login context (available auth methods and primary method)
 * @param {{tenant_subdomain?: string, tenant_id?: string|number}} params
 */
export const fetchLoginContext = createAsyncThunk(
  "auth/fetchLoginContext",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await authAPI.getLoginContext(params);
      const attributes = response?.data?.attributes || {};
      return {
        tenantResolved: !!attributes.tenant_resolved,
        tenant: attributes.tenant || null,
        branding: attributes.tenant_resolved ? attributes.branding || null : null,
        primaryMethod: attributes.primary_method || "password",
        methods: Array.isArray(attributes.methods) ? attributes.methods : [],
        passwordFailsafeActive: !!attributes.password_failsafe_active,
        passwordFailsafeExpiresAt: attributes.password_failsafe_expires_at || null,
        ssoFeatureEnabled: !!attributes.sso_feature_enabled,
        forcedByPlan: !!attributes.forced_by_plan,
        requiresTenantInput: !!attributes.requires_tenant_input,
        passwordLoginAvailable: passwordLoginAvailableFrom(attributes),
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Whether a sign-in response (password, magic link or OAuth) stops at the
 * MFA challenge instead of starting a session.
 * @param {Object} response - the JSON:API sign-in response
 * @returns {boolean}
 */
function isMfaRequired(response) {
  return Boolean(response?.mfa_required || response?.data?.attributes?.mfa_required);
}

/**
 * Login with email and password
 * Normalizes JSON:API responses using centralized utility
 * Fetches tenants separately if not included in login response
 * @param {{email: string, password: string}} credentials
 */
export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password, tenantSubdomain, rememberMe = false }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await authAPI.login(
        email,
        password,
        tenantSubdomain,
        rememberMe
      );

      // Check if MFA is required before full login completes
      if (isMfaRequired(jsonApiResponse)) {
        return { mfa_required: true };
      }

      // Normalize user data from JSON:API format
      // @ts-ignore - API returns Object but has data property
      const user = normalizeData(jsonApiResponse.data);

      // NOTE: We don't call processIncluded() here to avoid circular dependency
      // The tenant and tenant_membership data will be extracted manually below

      // Extract and normalize tenants and tenant_memberships from included section
      let tenants = [];
      let tenantMemberships = [];
      // @ts-ignore - API returns Object but has included property
      if (jsonApiResponse.included && Array.isArray(jsonApiResponse.included)) {
        const included = normalizeIncluded(jsonApiResponse.included);

        // Get tenants from the normalized included map
        if (included.tenant) {
          tenants = Object.values(included.tenant);
        }

        // Get tenant_memberships from the normalized included map
        if (included.tenant_membership) {
          tenantMemberships = Object.values(included.tenant_membership);
        }
      }

      if (tenants.length === 0) {
        console.warn('[login] No tenants in included section');
      }

      // Store token if provided (session-based auth doesn't use tokens, but keep for future)
      // @ts-ignore - API returns Object but has token property
      if (jsonApiResponse.token) {
        // @ts-ignore
        localStorage.setItem("auth_token", jsonApiResponse.token);
      }

      return {
        user,
        tenants,
        tenantMemberships,
        // @ts-ignore - API returns Object but has token property
        token: jsonApiResponse.token,
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Initiate OAuth login flow
 * @param {string} provider - OAuth provider (google, github, etc.)
 */
export const initiateOAuth = createAsyncThunk(
  "auth/initiateOAuth",
  async (payload, { rejectWithValue }) => {
    try {
      const provider = typeof payload === "string" ? payload : payload?.provider;
      const tenantSubdomain = typeof payload === "string" ? undefined : payload?.tenantSubdomain;
      const response = await authAPI.initiateOAuth(provider, tenantSubdomain);
      const authorizationUrl =
        response?.data?.attributes?.authorization_url ||
        response?.authorization_url ||
        response?.data?.authorization_url;
      if (!authorizationUrl) {
        throw new Error("OAuth authorization URL missing from response");
      }
      // Redirect to OAuth provider
      window.location.href = authorizationUrl;
      return null;
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Handle OAuth callback
 * @param {{code: string, state: string}} params - OAuth callback parameters
 */
export const handleOAuthCallback = createAsyncThunk(
  "auth/handleOAuthCallback",
  async ({ code, state }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await authAPI.handleOAuthCallback(code, state);
      // An MFA-enabled account gets the same challenge step as a password login.
      if (isMfaRequired(jsonApiResponse)) {
        return { mfa_required: true };
      }
      const user = normalizeData(jsonApiResponse.data);

      let tenants = [];
      let tenantMemberships = [];
      if (jsonApiResponse.included && Array.isArray(jsonApiResponse.included)) {
        const included = normalizeIncluded(jsonApiResponse.included);
        if (included.tenant) {
          tenants = Object.values(included.tenant);
        }
        if (included.tenant_membership) {
          tenantMemberships = Object.values(included.tenant_membership);
        }
      }
      return {
        user,
        tenants,
        tenantMemberships,
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Logout current user
 */
export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await authAPI.logout();
      // Clear all auth-related storage
      localStorage.removeItem("auth_token");
      localStorage.removeItem("persist:graphit-auth");
      sessionStorage.clear();
      return null;
    } catch (error) {
      // Still clear local state even if API call fails
      localStorage.removeItem("auth_token");
      localStorage.removeItem("persist:graphit-auth");
      sessionStorage.clear();
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Update user profile information
 * @param {Object} data - Profile data to update
 */
export const updateUserProfile = createAsyncThunk(
  "auth/updateProfile",
  async (data, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await authAPI.updateProfile(data);
      // @ts-ignore - API returns Object but has data property
      const user = normalizeData(jsonApiResponse.data);
      return user;
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Upload user profile photo
 * @param {File} file - Profile photo file
 */
export const uploadUserProfilePhoto = createAsyncThunk(
  "auth/uploadProfilePhoto",
  async (file, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await authAPI.uploadProfilePhoto(file);
      // @ts-ignore - API returns Object but has data property
      const user = normalizeData(jsonApiResponse.data);
      return user;
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Change user password
 * @param {Object} data - Password change data
 */
export const changeUserPassword = createAsyncThunk(
  "auth/changePassword",
  async (data, { rejectWithValue }) => {
    try {
      await authAPI.changePassword(data);
      return null;
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Signup new user with tenant creation
 * Also supports existing users creating additional tenants
 * @param {Object} data - Signup data
 */
export const signup = createAsyncThunk(
  "auth/signup",
  async (data, { rejectWithValue }) => {
    try {
      const response = await authAPI.signup(data);
      return {
        message: response.data.attributes.message,
        email: response.data.attributes.email,
        existing_user: response.data.attributes.existing_user || false,
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Fetch the current user's active pending signups (reservations awaiting a plan).
 */
export const fetchPendingSignups = createAsyncThunk(
  "auth/fetchPendingSignups",
  async (_, { rejectWithValue }) => {
    try {
      const res = await authAPI.getPendingSignups();
      return res.pending_signups || [];
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Fetch organizations the current user owns that are deactivated
 * (self-service reactivate flow, #187 follow-up).
 */
export const fetchDeactivatedTenants = createAsyncThunk(
  "auth/fetchDeactivatedTenants",
  async (_, { rejectWithValue }) => {
    try {
      const res = await tenantsAPI.getDeactivated();
      return res.data || [];
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Reactivate a deactivated organization the user owns, then refresh the
 * tenant/membership list so it reappears, and select it as current.
 */
export const reactivateTenant = createAsyncThunk(
  "auth/reactivateTenant",
  async (tenantId, { dispatch, rejectWithValue }) => {
    try {
      await tenantsAPI.reactivate(tenantId);
      await dispatch(fetchCurrentUser()).unwrap();
      return { tenantId };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Reserve a new organization for the signed-in user.
 *
 * Used by the tenant-less user who chooses "Create an organization": they are
 * already authenticated, so only org details are collected. Resolves with the
 * created reservation, which the plan-selection page then picks up.
 */
export const reserveOrganization = createAsyncThunk(
  "auth/reserveOrganization",
  async ({ organizationName, subdomain }, { rejectWithValue }) => {
    try {
      const res = await authAPI.reserveOrganization({
        organization_name: organizationName,
        subdomain,
      });
      return res.pending_signup;
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Start the free trial for a pending signup.
 *
 * The idempotent backend answers 202 `{status: "provisioning"}` while the
 * workspace is built in a task (follow `signup/status/`), and 200/201
 * `{status: "completed", tenant}` once it exists. An older backend answers
 * 201 `{tenant}` after building in-request; that is "completed" too.
 * Resolves with `{status, tenant, pendingSignupId}`; `tenant` is null while
 * provisioning.
 */
export const selectTrial = createAsyncThunk(
  "auth/selectTrial",
  async (pendingSignupId, { rejectWithValue }) => {
    try {
      const res = await authAPI.selectTrialPlan(pendingSignupId);
      const tenant = res?.tenant || null;
      const status = tenant ? SIGNUP_STATUS_COMPLETED : SIGNUP_STATUS_PROVISIONING;
      return { status, tenant, pendingSignupId };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Start Stripe Checkout for a paid plan during signup and redirect to Stripe.
 * Mirrors subscriptions/initiateCheckout (page-driven redirect).
 */
export const startSignupCheckout = createAsyncThunk(
  "auth/startSignupCheckout",
  async ({ pendingSignupId, planId, billingCycle }, { rejectWithValue }) => {
    try {
      const res = await authAPI.createSignupCheckout({ pendingSignupId, planId, billingCycle });
      window.location.href = res.checkout_url;
      return res;
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Single-shot provisioning-status poll for a pending signup. The page owns the
 * interval; the backend self-heals a lost webhook on each call.
 */
export const pollSignupStatus = createAsyncThunk(
  "auth/pollSignupStatus",
  async (pendingSignupId, { rejectWithValue }) => {
    try {
      return await authAPI.getSignupStatus(pendingSignupId);
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Find the workspace a consumed reservation turned into.
 *
 * Starting a trial on a reservation an earlier (timed-out) attempt already
 * used returns 404 from a backend that is not idempotent yet. The work
 * usually succeeded: ask the status endpoint (it still reports a completed
 * reservation with its tenant), and failing that reload the user's
 * memberships and take any tenant that was not there before.
 *
 * Resolves with the tenant; rejects when no new workspace can be found.
 */
export const recoverProvisionedSignup = createAsyncThunk(
  "auth/recoverProvisionedSignup",
  async (pendingSignupId, { dispatch, getState, rejectWithValue }) => {
    const status = await dispatch(pollSignupStatus(pendingSignupId));
    if (pollSignupStatus.fulfilled.match(status) && status.payload?.status === "completed" && status.payload.tenant) {
      return status.payload.tenant;
    }

    const knownTenantIds = new Set(selectTenants(getState()).map((tenant) => String(tenant.id)));
    await dispatch(fetchCurrentUser());
    const newTenant = selectTenants(getState()).find((tenant) => !knownTenantIds.has(String(tenant.id)));
    if (newTenant) return newTenant;

    return rejectWithValue({
      message: "This reservation is no longer available. Please sign up again to get started.",
    });
  }
);

/**
 * A verification token is single-use server-side, so two callers racing on
 * the same token (React StrictMode's double effect invoke; a route that
 * mounts the page twice) must share one HTTP request rather than each firing
 * their own - the second request would find the token already consumed and
 * get a 400 that has nothing to do with whether verification actually
 * succeeded. Caching the request promise per token means every caller for
 * that token resolves (or rejects) from the exact same outcome. Module-level
 * by design: it needs to survive across the double mount, and a token is
 * only ever verified once per page load anyway.
 *
 * A rejected request must NOT stay cached: a transient failure (network
 * blip, backend 500) still leaves the token unconsumed server-side, so the
 * user has to be able to retry with the same token. Only a fulfilled
 * request represents a token that's actually been spent, so only that
 * outcome is worth remembering.
 * @param {Map<string, Promise>} cache
 * @param {string} key
 * @param {() => Promise} makeRequest
 * @returns {Promise}
 */
function dedupedRequest(cache, key, makeRequest) {
  let request = cache.get(key);
  if (!request) {
    request = makeRequest().catch((error) => {
      cache.delete(key);
      throw error;
    });
    cache.set(key, request);
  }
  return request;
}

const verifyEmailRequests = new Map();

/**
 * Verify email with token and auto-login
 * @param {string} token - Email verification token
 */
export const verifyEmail = createAsyncThunk(
  "auth/verifyEmail",
  async (token, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await dedupedRequest(verifyEmailRequests, token, () => authAPI.verifyEmail(token));

      // Normalize user data from JSON:API format
      const user = normalizeData(jsonApiResponse.data);

      // Extract tenants and tenant_memberships from included section
      let tenants = [];
      let tenantMemberships = [];
      if (jsonApiResponse.included && Array.isArray(jsonApiResponse.included)) {
        const included = normalizeIncluded(jsonApiResponse.included);

        if (included.tenant) {
          tenants = Object.values(included.tenant);
        }

        if (included.tenant_membership) {
          tenantMemberships = Object.values(included.tenant_membership);
        }
      }

      return {
        user,
        tenants,
        tenantMemberships,
        // Pay-during-signup: backend attaches an active reservation here so we
        // can route a verified, tenant-less user straight to plan selection.
        pendingSignup: jsonApiResponse.meta?.pending_signup || null,
        // Trial being built in the background (graphit-backend#401): the page
        // shows real progress by polling the signup status with this id.
        provisioning: jsonApiResponse.meta?.provisioning || null,
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Resend verification email
 * @param {string} email - Email address to resend verification to
 */
export const resendVerificationEmail = createAsyncThunk(
  "auth/resendVerificationEmail",
  async (email, { rejectWithValue }) => {
    try {
      const response = await authAPI.resendVerificationEmail(email);
      return {
        message: response.data.attributes.message,
        email: response.data.attributes.email,
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Check subdomain availability
 * @param {string} subdomain - Subdomain to check
 */
export const checkSubdomainAvailability = createAsyncThunk(
  "auth/checkSubdomainAvailability",
  async (subdomain, { rejectWithValue }) => {
    try {
      const response = await authAPI.checkSubdomain(subdomain);
      return {
        subdomain: response.data.attributes.subdomain,
        available: response.data.attributes.available,
        suggestion: response.data.attributes.suggestion,
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Switch to a different tenant
 * Calls the backend API to update the session's current_tenant_id
 * Then updates Redux state with the new tenant
 * @param {Object} tenant - The tenant object to switch to (must have id and name)
 */
export const switchTenant = createAsyncThunk(
  "auth/switchTenant",
  async (tenant, { rejectWithValue }) => {
    try {
      // Call backend to update session
      const response = await authAPI.switchTenant(tenant.id);

      // Return tenant info for Redux state update
      return {
        tenantId: tenant.id,
        tenantName: response.data?.attributes?.tenant_name || tenant.name,
        role: response.data?.attributes?.role,
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Request a magic link email for passwordless login
 * @param {string|{email: string, tenantSubdomain?: string}} input - Email and optional verified tenant context
 */
export const requestMagicLink = createAsyncThunk(
  "auth/requestMagicLink",
  async (input, { rejectWithValue }) => {
    const { email, tenantSubdomain } = typeof input === 'string' ? { email: input } : input;
    try {
      const response = tenantSubdomain
        ? await authAPI.requestMagicLink(email, tenantSubdomain)
        : await authAPI.requestMagicLink(email);
      const attributes = response?.data?.attributes || {};
      return {
        email,
        message: attributes.message,
        showPasswordOption: !!attributes.show_password_option,
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

const verifyMagicLinkRequests = new Map();

/**
 * Verify a magic link token and login
 * @param {string} token - Magic link token from URL
 */
export const verifyMagicLink = createAsyncThunk(
  "auth/verifyMagicLink",
  async (token, { rejectWithValue }) => {
    try {
      // Same single-use-token race as verifyEmail above.
      const jsonApiResponse = await dedupedRequest(verifyMagicLinkRequests, token, () => authAPI.verifyMagicLink(token));

      // Check if MFA is required before full login completes
      if (isMfaRequired(jsonApiResponse)) {
        return { mfa_required: true };
      }

      const user = normalizeData(jsonApiResponse.data);

      let tenants = [];
      let tenantMemberships = [];
      if (jsonApiResponse.included && Array.isArray(jsonApiResponse.included)) {
        const included = normalizeIncluded(jsonApiResponse.included);
        if (included.tenant) {
          tenants = Object.values(included.tenant);
        }
        if (included.tenant_membership) {
          tenantMemberships = Object.values(included.tenant_membership);
        }
      }

      return {
        user,
        tenants,
        tenantMemberships,
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Fetch all available tenants for superusers
 * This is used when a superuser has no tenant memberships but needs to see all tenants
 */
export const fetchAllTenants = createAsyncThunk(
  "auth/fetchAllTenants",
  async (_, { rejectWithValue }) => {
    try {
      // This endpoint returns tenant_memberships which include tenant data
      // For superusers, it returns synthetic memberships for all tenants
      const response = await tenantsAPI.getAll();

      // Extract tenants from the tenant_memberships response
      let tenants = [];

      // The response contains tenant_memberships with related tenants
      if (response.data && Array.isArray(response.data)) {
        // Extract tenant IDs from relationships and tenants from included
        const tenantIds = new Set();
        response.data.forEach((membership) => {
          if (membership.relationships?.tenant?.data?.id) {
            tenantIds.add(membership.relationships.tenant.data.id);
          }
        });

        // Get tenant details from included section
        if (response.included && Array.isArray(response.included)) {
          tenants = response.included
            .filter((item) => item.type === 'tenant')
            .map((item) => normalizeResource(item));
        }
      }

      return { tenants };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Fetch MFA status for the current user
 */
export const fetchMFAStatus = createAsyncThunk(
  "auth/fetchMFAStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.getMFAStatus();
      // Support both JSON:API wrapped and plain responses
      const attrs = response?.data?.attributes || response;
      return {
        is_enabled: !!attrs.is_enabled,
        backup_codes_remaining: attrs.backup_codes_remaining ?? null,
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Begin MFA setup — returns secret and otpauth_uri
 */
export const setupMFA = createAsyncThunk(
  "auth/setupMFA",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.setupMFA();
      const attrs = response?.data?.attributes || response;
      return {
        secret: attrs.secret,
        otpauth_uri: attrs.otpauth_uri,
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Verify MFA setup with TOTP code — returns backup codes
 * @param {string} code - 6-digit TOTP code
 */
export const verifyMFASetup = createAsyncThunk(
  "auth/verifyMFASetup",
  async (code, { rejectWithValue }) => {
    try {
      const response = await authAPI.verifyMFASetup(code);
      const attrs = response?.data?.attributes || response;
      return {
        backup_codes: attrs.backup_codes,
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Disable MFA for the current user
 * @param {string} code - TOTP code to confirm disable
 */
export const disableMFA = createAsyncThunk(
  "auth/disableMFA",
  async (code, { rejectWithValue }) => {
    try {
      await authAPI.disableMFA(code);
      return null;
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * Complete MFA challenge during login
 * @param {string} code - 6-digit TOTP code or 8-char backup code
 */
export const challengeMFA = createAsyncThunk(
  "auth/challengeMFA",
  async (code, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await authAPI.challengeMFA(code);

      // Normalize user data from JSON:API format
      const user = normalizeData(jsonApiResponse.data);

      let tenants = [];
      let tenantMemberships = [];
      if (jsonApiResponse.included && Array.isArray(jsonApiResponse.included)) {
        const included = normalizeIncluded(jsonApiResponse.included);
        if (included.tenant) {
          tenants = Object.values(included.tenant);
        }
        if (included.tenant_membership) {
          tenantMemberships = Object.values(included.tenant_membership);
        }
      }

      if (jsonApiResponse.token) {
        localStorage.setItem("auth_token", jsonApiResponse.token);
      }

      return {
        user,
        tenants,
        tenantMemberships,
        token: jsonApiResponse.token,
      };
    } catch (error) {
      return rejectWithValue(error.apiError || { message: error.message });
    }
  }
);

/**
 * @typedef {Object} AuthState
 * @property {User|null} user - Current authenticated user
 * @property {string|null} currentTenantId - ID of currently selected tenant
 * @property {Object} tenants - Normalized tenants (entity adapter)
 * @property {Object} tenantMemberships - Normalized tenant memberships (entity adapter)
 * @property {boolean} isAuthenticated - Authentication status
 * @property {boolean} isLoading - Loading state for async operations
 * @property {string|null} error - Error message if any
 * @property {boolean} needsEmailVerification - User signed up and needs email verification
 * @property {string|null} verificationEmail - Email address pending verification
 * @property {Object} subdomainCheck - Subdomain availability check state
 */

/** @type {AuthState} */
const initialState = {
  user: null,
  currentTenantId: null, // Store only the ID, not the full object
  // Superuser impersonation from /auth/me (see ./impersonation.js). Not
  // persisted: only the server knows whether it still holds.
  impersonation: null,
  tenants: tenantsAdapter.getInitialState(),
  tenantMemberships: tenantMembershipsAdapter.getInitialState(),
  isAuthenticated: false,
  isLoading: false,
  // Set once /auth/me has answered in this page load. Not persisted: a
  // rehydrated `isAuthenticated` is only a guess until the server confirms it.
  // ProtectedRoute blocks on the loader only until then, so a later refresh
  // (fetchCurrentUser after a signup, MFA, settings) never unmounts the page
  // that asked for it.
  isSessionVerified: false,
  error: null,
  loginContext: {
    branding: null,
    tenantResolved: false,
    tenant: null,
    primaryMethod: "password",
    methods: [],
    passwordFailsafeActive: false,
    passwordFailsafeExpiresAt: null,
    ssoFeatureEnabled: false,
    forcedByPlan: false,
    requiresTenantInput: true,
    passwordLoginAvailable: true,
    loading: false,
    error: null,
  },
  magicLink: {
    sent: false,
    email: null,
    showPasswordOption: false,
    loading: false,
    error: null,
  },
  mfa: {
    required: false,
    status: null, // { is_enabled, backup_codes_remaining } or null
    setupData: null, // { secret, otpauth_uri } or null
    backupCodes: null, // string[] or null
    loading: false,
    error: null,
  },
  needsEmailVerification: false,
  verificationEmail: null,
  subdomainCheck: {
    subdomain: null,
    available: null,
    checking: false,
    suggestion: null,
  },
  // Pay-during-signup: reservations awaiting a plan + the plan-selection flow state
  pendingSignups: [],
  pendingSignupsLoaded: false,
  // Owner's deactivated organizations, for self-service reactivation (#187 follow-up)
  deactivatedTenants: [],
  deactivatedTenantsLoaded: false,
  reactivatingTenantId: null,
  signupPlan: {
    checkoutLoading: false,
    status: null, // provisioning status from pollSignupStatus
    provisionedTenant: null,
    error: null,
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Set the current authenticated user
     * @param {AuthState} state
     * @param {Object} action
     * @param {User} action.payload
     */
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.error = null;
    },

    /**
     * Set the current tenant context
     * @param {AuthState} state
     * @param {Object} action
     * @param {Tenant|string} action.payload - Tenant object or tenant ID
     */
    setTenant: (state, action) => {
      // Accept either tenant object or ID string
      state.currentTenantId = typeof action.payload === 'string'
        ? action.payload
        : action.payload?.id;
    },

    /**
     * Set the list of accessible tenants (legacy - for backward compatibility)
     * @param {AuthState} state
     * @param {Object} action
     * @param {Array<Tenant>} action.payload
     */
    setTenants: (state, action) => {
      tenantsAdapter.setAll(state.tenants, action.payload);
    },

    /**
     * Upsert many tenants (for entity processor)
     */
    upsertManyTenants: (state, action) => {
      tenantsAdapter.upsertMany(state.tenants, action.payload);
    },

    /**
     * Upsert many tenant memberships (for entity processor)
     */
    upsertManyTenantMemberships: (state, action) => {
      tenantMembershipsAdapter.upsertMany(state.tenantMemberships, action.payload);
    },

    /**
     * Set loading state
     * @param {AuthState} state
     * @param {Object} action
     * @param {boolean} action.payload
     */
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    /**
     * Set error message
     * @param {AuthState} state
     * @param {Object} action
     * @param {string|null} action.payload
     */
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    /**
     * Reset magic link state
     */
    resetMagicLink: (state) => {
      state.magicLink = { ...initialState.magicLink };
    },

    /**
     * Reset MFA state (e.g. when navigating away from MFA challenge)
     */
    resetMFA: (state) => {
      state.mfa = { ...initialState.mfa };
    },

    /**
     * Clear auth state and logout
     * @param {AuthState} state
     */
    logout: (state) => {
      state.user = null;
      state.currentTenantId = null;
      state.impersonation = null;
      tenantsAdapter.removeAll(state.tenants);
      tenantMembershipsAdapter.removeAll(state.tenantMemberships);
      state.isAuthenticated = false;
      state.isSessionVerified = false;
      state.isLoading = false;
      state.error = null;
      state.mfa = { ...initialState.mfa };
      state.loginContext = { ...initialState.loginContext };
    },

    /**
     * Remove one tenant (and its membership) after the user deactivates or
     * deletes their own organization from Danger Zone. Unlike logout, the
     * user stays authenticated - they only lose access to this one tenant.
     * @param {AuthState} state
     * @param {Object} action
     * @param {{tenantId: string}} action.payload
     */
    tenantRemoved: (state, action) => {
      const { tenantId } = action.payload;
      tenantsAdapter.removeOne(state.tenants, tenantId);

      const membershipIdsToRemove = Object.values(state.tenantMemberships.entities)
        .filter((m) => m.tenant === tenantId || m.tenant_id === tenantId || m.tenant?.id === tenantId)
        .map((m) => m.id);
      tenantMembershipsAdapter.removeMany(state.tenantMemberships, membershipIdsToRemove);

      // Auto-select if exactly one tenant remains, same as the post-login
      // auto-select; otherwise force the picker.
      const remainingTenantIds = state.tenants.ids;
      state.currentTenantId = remainingTenantIds.length === 1 ? remainingTenantIds[0] : null;
    },
  },
  extraReducers: (builder) => {
    // fetchLoginContext
    builder.addCase(fetchLoginContext.pending, (state) => {
      state.loginContext.loading = true;
      state.loginContext.error = null;
    });
    builder.addCase(fetchLoginContext.fulfilled, (state, action) => {
      state.loginContext = {
        ...state.loginContext,
        ...action.payload,
        loading: false,
        error: null,
      };
    });
    builder.addCase(fetchLoginContext.rejected, (state, action) => {
      state.loginContext.loading = false;
      state.loginContext.error = action.payload?.message || "Failed to resolve login context";
    });

    // Impersonation ended (useExitImpersonation). The tenant is never left
    // empty in between - that would bounce ProtectedRoute to /tenant-select
    // until the refetch lands. The backend's restored tenant is adopted when
    // it sent one; otherwise the pinned id stays until the /auth/me refetch,
    // which drops it if the admin isn't a member and falls back to their
    // remembered or only tenant.
    builder.addCase(IMPERSONATION_EXITED, (state, action) => {
      state.impersonation = null;
      const restoredTenantId = action.payload?.restoredTenantId;
      if (restoredTenantId) state.currentTenantId = String(restoredTenantId);
    });

    // fetchCurrentUser
    builder.addCase(fetchCurrentUser.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchCurrentUser.fulfilled, (state, action) => {
      state.isSessionVerified = true;
      state.user = action.payload.user;
      tenantsAdapter.setAll(state.tenants, action.payload.tenants);
      tenantMembershipsAdapter.setAll(state.tenantMemberships, action.payload.tenantMemberships);
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;

      // Impersonating: the backend pins the session to the target's tenant,
      // which need not be one of the admin's own memberships. Pin it here
      // too and skip the remembered-tenant logic below.
      const impersonation = action.payload.impersonation || null;
      state.impersonation = impersonation;
      if (impersonation) {
        tenantsAdapter.upsertOne(state.tenants, impersonatedTenantEntity(impersonation));
        state.currentTenantId = impersonation.tenant.id;
        return;
      }

      // Resolve which tenant is active. Compare as strings — tenant ids arrive
      // from JSON:API as strings, and the remembered id comes from localStorage.
      const tenantIds = state.tenants.ids.map(String);

      // Drop a selection that is no longer valid for this user, e.g. a tenant
      // they were removed from, or a stale value from a previous account on
      // this browser. Otherwise selectTenant returns null and the header shows
      // "No tenant selected" even though a real choice is available.
      if (state.currentTenantId && !tenantIds.includes(String(state.currentTenantId))) {
        state.currentTenantId = null;
      }

      if (!state.currentTenantId) {
        const remembered = action.payload.rememberedTenantId;
        if (remembered && tenantIds.includes(String(remembered))) {
          // Multi-tenant user returning to their last-used organization (#167).
          state.currentTenantId = remembered;
        } else if (state.tenants.ids.length === 1) {
          // Single-tenant users have no meaningful choice.
          state.currentTenantId = state.tenants.ids[0];
        }
      }
    });
    builder.addCase(fetchCurrentUser.rejected, (state, action) => {
      // Clear auth state if fetching current user fails (session expired/invalid)
      state.isSessionVerified = false;
      state.user = null;
      state.currentTenantId = null;
      state.impersonation = null;
      tenantsAdapter.removeAll(state.tenants);
      tenantMembershipsAdapter.removeAll(state.tenantMemberships);
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = action.payload?.message || "Failed to fetch user";
    });

    // login
    builder.addCase(login.pending, (state) => {
      state.isLoading = true;
      state.error = null;
      state.mfa.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      // If MFA is required, don't complete login — set mfa.required flag
      if (action.payload.mfa_required) {
        state.mfa.required = true;
        state.isLoading = false;
        state.error = null;
        return;
      }

      state.user = action.payload.user;
      tenantsAdapter.setAll(state.tenants, action.payload.tenants);
      if (action.payload.tenantMemberships) {
        tenantMembershipsAdapter.setAll(state.tenantMemberships, action.payload.tenantMemberships);
      }
      state.isAuthenticated = true;
      state.isSessionVerified = true;
      state.isLoading = false;
      state.error = null;
      // Auto-select ONLY if user has exactly one tenant
      const tenantIds = state.tenants.ids;
      if (tenantIds.length === 1 && !state.currentTenantId) {
        state.currentTenantId = tenantIds[0];
      }
    });
    builder.addCase(login.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message || "Login failed";
    });

    // initiateOAuth
    builder.addCase(initiateOAuth.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(initiateOAuth.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message || "Failed to initiate OAuth";
    });

    // handleOAuthCallback
    builder.addCase(handleOAuthCallback.pending, (state) => {
      state.isLoading = true;
      state.error = null;
      state.mfa.error = null;
    });
    builder.addCase(handleOAuthCallback.fulfilled, (state, action) => {
      // If MFA is required, don't complete login — set mfa.required flag
      if (action.payload.mfa_required) {
        state.mfa.required = true;
        state.isLoading = false;
        state.error = null;
        return;
      }

      state.user = action.payload.user;
      tenantsAdapter.setAll(state.tenants, action.payload.tenants);
      if (action.payload.tenantMemberships) {
        tenantMembershipsAdapter.setAll(state.tenantMemberships, action.payload.tenantMemberships);
      }
      state.isAuthenticated = true;
      state.isSessionVerified = true;
      state.isLoading = false;
      state.error = null;
      // Auto-select ONLY if user has exactly one tenant
      const tenantIds = state.tenants.ids;
      if (tenantIds.length === 1) {
        state.currentTenantId = tenantIds[0];
      }
    });
    builder.addCase(handleOAuthCallback.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message || "OAuth authentication failed";
    });

    // logoutUser
    builder.addCase(logoutUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.user = null;
      state.currentTenantId = null;
      state.impersonation = null;
      tenantsAdapter.removeAll(state.tenants);
      tenantMembershipsAdapter.removeAll(state.tenantMemberships);
      state.isAuthenticated = false;
      state.isSessionVerified = false;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(logoutUser.rejected, (state) => {
      // Still clear state even if API call failed
      state.user = null;
      state.currentTenantId = null;
      state.impersonation = null;
      tenantsAdapter.removeAll(state.tenants);
      tenantMembershipsAdapter.removeAll(state.tenantMemberships);
      state.isAuthenticated = false;
      state.isSessionVerified = false;
      state.isLoading = false;
    });

    // updateUserProfile
    builder.addCase(updateUserProfile.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateUserProfile.fulfilled, (state, action) => {
      // @ts-ignore - normalizeData returns correct user type
      state.user = action.payload;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(updateUserProfile.rejected, (state, action) => {
      state.isLoading = false;
      // @ts-ignore - payload has message property
      state.error = action.payload?.message || "Failed to update profile";
    });

    // uploadUserProfilePhoto
    builder.addCase(uploadUserProfilePhoto.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(uploadUserProfilePhoto.fulfilled, (state, action) => {
      // @ts-ignore - normalizeData returns correct user type
      state.user = action.payload;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(uploadUserProfilePhoto.rejected, (state, action) => {
      state.isLoading = false;
      // @ts-ignore - payload has message property
      state.error = action.payload?.message || "Failed to upload profile photo";
    });

    // changeUserPassword
    builder.addCase(changeUserPassword.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(changeUserPassword.fulfilled, (state) => {
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(changeUserPassword.rejected, (state, action) => {
      state.isLoading = false;
      // @ts-ignore - payload has message property
      state.error = action.payload?.message || "Failed to change password";
    });

    // signup
    builder.addCase(signup.pending, (state) => {
      state.isLoading = true;
      state.error = null;
      state.needsEmailVerification = false;
      state.verificationEmail = null;
    });
    builder.addCase(signup.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.needsEmailVerification = true;
      state.verificationEmail = action.payload.email;
    });
    builder.addCase(signup.rejected, (state, action) => {
      state.isLoading = false;
      // @ts-ignore - payload has message property
      state.error = action.payload?.message || "Signup failed";
    });

    // verifyEmail
    builder.addCase(verifyEmail.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(verifyEmail.fulfilled, (state, action) => {
      state.user = action.payload.user;
      tenantsAdapter.setAll(state.tenants, action.payload.tenants);
      if (action.payload.tenantMemberships) {
        tenantMembershipsAdapter.setAll(state.tenantMemberships, action.payload.tenantMemberships);
      }
      state.isAuthenticated = true;
      state.isSessionVerified = true;
      state.isLoading = false;
      state.error = null;
      state.needsEmailVerification = false;
      state.verificationEmail = null;
      // Auto-select ONLY if user has exactly one tenant
      const tenantIds = state.tenants.ids;
      if (tenantIds.length === 1 && !state.currentTenantId) {
        state.currentTenantId = tenantIds[0];
      }
      // Seed pending-signup state from verify-email meta so guards can route to
      // plan selection without waiting for a separate fetch.
      if (action.payload.pendingSignup) {
        state.pendingSignups = [action.payload.pendingSignup];
        state.pendingSignupsLoaded = true;
      }
    });
    builder.addCase(verifyEmail.rejected, (state, action) => {
      state.isLoading = false;
      // @ts-ignore - payload has message property
      state.error = action.payload?.message || "Email verification failed";
    });

    // resendVerificationEmail
    builder.addCase(resendVerificationEmail.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(resendVerificationEmail.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.verificationEmail = action.payload.email;
    });
    builder.addCase(resendVerificationEmail.rejected, (state, action) => {
      state.isLoading = false;
      // @ts-ignore - payload has message property
      state.error = action.payload?.message || "Failed to resend verification email";
    });

    // checkSubdomainAvailability
    builder.addCase(checkSubdomainAvailability.pending, (state) => {
      state.subdomainCheck.checking = true;
    });
    builder.addCase(checkSubdomainAvailability.fulfilled, (state, action) => {
      state.subdomainCheck.subdomain = action.payload.subdomain;
      state.subdomainCheck.available = action.payload.available;
      state.subdomainCheck.suggestion = action.payload.suggestion;
      state.subdomainCheck.checking = false;
    });
    builder.addCase(checkSubdomainAvailability.rejected, (state) => {
      state.subdomainCheck.checking = false;
      state.subdomainCheck.available = null;
    });

    // switchTenant
    builder.addCase(switchTenant.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(switchTenant.fulfilled, (state, action) => {
      state.currentTenantId = action.payload.tenantId;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(switchTenant.rejected, (state, action) => {
      state.isLoading = false;
      // @ts-ignore - payload has message property
      state.error = action.payload?.message || "Failed to switch tenant";
    });

    // fetchAllTenants (for superusers)
    builder.addCase(fetchAllTenants.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchAllTenants.fulfilled, (state, action) => {
      tenantsAdapter.setAll(state.tenants, action.payload.tenants);
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(fetchAllTenants.rejected, (state, action) => {
      state.isLoading = false;
      // @ts-ignore - payload has message property
      state.error = action.payload?.message || "Failed to fetch tenants";
    });

    // requestMagicLink
    builder.addCase(requestMagicLink.pending, (state) => {
      state.magicLink.loading = true;
      state.magicLink.error = null;
    });
    builder.addCase(requestMagicLink.fulfilled, (state, action) => {
      state.magicLink.sent = true;
      state.magicLink.email = action.payload.email;
      state.magicLink.showPasswordOption = action.payload.showPasswordOption;
      state.magicLink.loading = false;
      state.magicLink.error = null;
    });
    builder.addCase(requestMagicLink.rejected, (state, action) => {
      state.magicLink.loading = false;
      state.magicLink.error = action.payload?.message || "Failed to send magic link";
    });

    // verifyMagicLink
    builder.addCase(verifyMagicLink.pending, (state) => {
      state.isLoading = true;
      state.error = null;
      state.mfa.error = null;
    });
    builder.addCase(verifyMagicLink.fulfilled, (state, action) => {
      // If MFA is required, don't complete login — set mfa.required flag
      if (action.payload.mfa_required) {
        state.mfa.required = true;
        state.isLoading = false;
        state.error = null;
        state.magicLink = { ...initialState.magicLink };
        return;
      }

      state.user = action.payload.user;
      tenantsAdapter.setAll(state.tenants, action.payload.tenants);
      if (action.payload.tenantMemberships) {
        tenantMembershipsAdapter.setAll(state.tenantMemberships, action.payload.tenantMemberships);
      }
      state.isAuthenticated = true;
      state.isSessionVerified = true;
      state.isLoading = false;
      state.error = null;
      state.magicLink = { ...initialState.magicLink };
      // Auto-select ONLY if user has exactly one tenant
      const tenantIds = state.tenants.ids;
      if (tenantIds.length === 1 && !state.currentTenantId) {
        state.currentTenantId = tenantIds[0];
      }
    });
    builder.addCase(verifyMagicLink.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message || "Magic link verification failed";
    });

    // challengeMFA — completes login after MFA verification
    builder.addCase(challengeMFA.pending, (state) => {
      state.mfa.loading = true;
      state.mfa.error = null;
    });
    builder.addCase(challengeMFA.fulfilled, (state, action) => {
      state.user = action.payload.user;
      tenantsAdapter.setAll(state.tenants, action.payload.tenants);
      if (action.payload.tenantMemberships) {
        tenantMembershipsAdapter.setAll(state.tenantMemberships, action.payload.tenantMemberships);
      }
      state.isAuthenticated = true;
      state.isSessionVerified = true;
      state.mfa.required = false;
      state.mfa.loading = false;
      state.mfa.error = null;
      state.isLoading = false;
      state.error = null;
      // Auto-select ONLY if user has exactly one tenant
      const tenantIds = state.tenants.ids;
      if (tenantIds.length === 1 && !state.currentTenantId) {
        state.currentTenantId = tenantIds[0];
      }
    });
    builder.addCase(challengeMFA.rejected, (state, action) => {
      state.mfa.loading = false;
      state.mfa.error = action.payload?.message || "Invalid verification code";
    });

    // fetchMFAStatus
    builder.addCase(fetchMFAStatus.pending, (state) => {
      state.mfa.loading = true;
      state.mfa.error = null;
    });
    builder.addCase(fetchMFAStatus.fulfilled, (state, action) => {
      state.mfa.status = action.payload;
      state.mfa.loading = false;
      state.mfa.error = null;
    });
    builder.addCase(fetchMFAStatus.rejected, (state, action) => {
      state.mfa.loading = false;
      state.mfa.error = action.payload?.message || "Failed to fetch MFA status";
    });

    // setupMFA
    builder.addCase(setupMFA.pending, (state) => {
      state.mfa.loading = true;
      state.mfa.error = null;
    });
    builder.addCase(setupMFA.fulfilled, (state, action) => {
      state.mfa.setupData = action.payload;
      state.mfa.loading = false;
      state.mfa.error = null;
    });
    builder.addCase(setupMFA.rejected, (state, action) => {
      state.mfa.loading = false;
      state.mfa.error = action.payload?.message || "Failed to start MFA setup";
    });

    // verifyMFASetup
    builder.addCase(verifyMFASetup.pending, (state) => {
      state.mfa.loading = true;
      state.mfa.error = null;
    });
    builder.addCase(verifyMFASetup.fulfilled, (state, action) => {
      state.mfa.backupCodes = action.payload.backup_codes;
      state.mfa.setupData = null;
      state.mfa.status = { is_enabled: true, backup_codes_remaining: action.payload.backup_codes?.length ?? 10 };
      state.mfa.loading = false;
      state.mfa.error = null;
    });
    builder.addCase(verifyMFASetup.rejected, (state, action) => {
      state.mfa.loading = false;
      state.mfa.error = action.payload?.message || "Invalid verification code";
    });

    // disableMFA
    builder.addCase(disableMFA.pending, (state) => {
      state.mfa.loading = true;
      state.mfa.error = null;
    });
    builder.addCase(disableMFA.fulfilled, (state) => {
      state.mfa.status = { is_enabled: false, backup_codes_remaining: null };
      state.mfa.setupData = null;
      state.mfa.backupCodes = null;
      state.mfa.loading = false;
      state.mfa.error = null;
    });
    builder.addCase(disableMFA.rejected, (state, action) => {
      state.mfa.loading = false;
      state.mfa.error = action.payload?.message || "Failed to disable MFA";
    });

    // ---- Pay-during-signup: plan selection ----
    builder.addCase(fetchPendingSignups.fulfilled, (state, action) => {
      state.pendingSignups = action.payload;
      state.pendingSignupsLoaded = true;
    });
    builder.addCase(fetchPendingSignups.rejected, (state) => {
      // Treat a failed fetch as "loaded, none" so guards don't hang.
      state.pendingSignupsLoaded = true;
    });

    // A newly reserved org is an active reservation like any other, so the
    // plan-selection guards pick it up without a refetch.
    builder.addCase(reserveOrganization.fulfilled, (state, action) => {
      state.pendingSignups = [...state.pendingSignups, action.payload];
      state.pendingSignupsLoaded = true;
    });

    // ---- Owner self-service reactivation (#187 follow-up) ----
    builder.addCase(fetchDeactivatedTenants.fulfilled, (state, action) => {
      state.deactivatedTenants = action.payload;
      state.deactivatedTenantsLoaded = true;
    });
    builder.addCase(fetchDeactivatedTenants.rejected, (state) => {
      // Treat a failed fetch as "loaded, none" so guards don't hang.
      state.deactivatedTenantsLoaded = true;
    });

    builder.addCase(reactivateTenant.pending, (state, action) => {
      state.reactivatingTenantId = action.meta.arg;
    });
    builder.addCase(reactivateTenant.fulfilled, (state, action) => {
      const { tenantId } = action.payload;
      state.deactivatedTenants = state.deactivatedTenants.filter((t) => t.id !== tenantId);
      // fetchCurrentUser() (dispatched inside the thunk) already refreshed
      // tenants/memberships and may have auto-selected a remembered tenant;
      // the owner explicitly chose this one, so it wins.
      state.currentTenantId = tenantId;
      state.reactivatingTenantId = null;
    });
    builder.addCase(reactivateTenant.rejected, (state, action) => {
      state.reactivatingTenantId = null;
      state.error = action.payload?.message || "Failed to reactivate organization";
    });

    builder.addCase(selectTrial.pending, (state) => {
      state.signupPlan.checkoutLoading = true;
      state.signupPlan.error = null;
    });
    builder.addCase(selectTrial.fulfilled, (state, action) => {
      const { status, tenant } = action.payload;
      state.signupPlan.checkoutLoading = false;
      // Accepted but still building: keep the reservation until the status
      // poll reports the tenant.
      if (status !== SIGNUP_STATUS_COMPLETED) return;
      tenantsAdapter.upsertOne(state.tenants, tenant);
      state.currentTenantId = tenant.id;
      // Drop the just-provisioned reservation.
      state.pendingSignups = state.pendingSignups.filter(
        (p) => p.id !== action.payload.pendingSignupId
      );
      state.signupPlan.provisionedTenant = tenant;
    });
    builder.addCase(selectTrial.rejected, (state, action) => {
      state.signupPlan.checkoutLoading = false;
      // A timeout or gateway error usually means the server is still building
      // the workspace, and a 404/409 that an earlier attempt already built
      // it; the page waits or recovers instead of showing an error.
      if (classifyProvisioningError(action.payload) !== OUTCOME_FAILED) return;
      state.signupPlan.error = action.payload?.message || "Could not start your trial. Please try again.";
    });

    builder.addCase(recoverProvisionedSignup.rejected, (state, action) => {
      state.signupPlan.error = action.payload?.message || "Could not start your trial. Please try again.";
    });

    builder.addCase(startSignupCheckout.pending, (state) => {
      state.signupPlan.checkoutLoading = true;
      state.signupPlan.error = null;
    });
    builder.addCase(startSignupCheckout.rejected, (state, action) => {
      state.signupPlan.checkoutLoading = false;
      state.signupPlan.error = action.payload?.message || "Could not start checkout. Please try again.";
    });
    // fulfilled redirects to Stripe; no state change needed.

    builder.addCase(pollSignupStatus.fulfilled, (state, action) => {
      state.signupPlan.status = action.payload.status;
      // Only a reported error replaces the banner: a routine poll landing
      // just after a failed trial request must not wipe that failure away.
      if (action.payload.error_message) state.signupPlan.error = action.payload.error_message;
      if (action.payload.status === "completed" && action.payload.tenant) {
        tenantsAdapter.upsertOne(state.tenants, action.payload.tenant);
        state.currentTenantId = action.payload.tenant.id;
        state.signupPlan.provisionedTenant = action.payload.tenant;
      }
    });
  },
});

export const {
  setUser,
  setTenant,
  setTenants,
  upsertManyTenants,
  upsertManyTenantMemberships,
  setLoading,
  setError,
  resetMagicLink,
  resetMFA,
  logout,
  tenantRemoved
} = authSlice.actions;

/**
 * Make a just-created organization the current one, the way switching to any
 * other organization does.
 *
 * Goes through `switchTenant`, not a bare `setTenant`: its fulfilled action is
 * what resets every tenant-scoped slice (store/resetOnTenantSwitch.js),
 * clears the React Query cache, and resets the assistant's conversation.
 * The switch also updates the backend session. Local tenant changes clear
 * scoped state and the query cache through the store's tenant-change guards.
 *
 * If the switch call itself fails, fall back to `setTenant` so the user still
 * lands in the workspace they just created with the same scoped-state reset.
 *
 * @param {{id: string|number, name?: string}} tenant
 */
export const enterNewTenant = (tenant) => async (dispatch) => {
  const result = await dispatch(switchTenant({ ...tenant, id: String(tenant.id) }));
  if (switchTenant.rejected.match(result)) {
    dispatch(setTenant(String(tenant.id)));
  }
  return result;
};

// Export for entity processor registration
export const upsertMany = upsertManyTenants; // Alias for entity processor

// Entity adapter selectors for tenants
export const {
  selectAll: selectAllTenantsFromEntities,
  selectById: selectTenantById,
  selectIds: selectTenantIds,
  selectEntities: selectTenantEntities,
} = tenantsAdapter.getSelectors((state) => state.auth.tenants);

// Entity adapter selectors for tenant memberships
export const {
  selectAll: selectAllTenantMemberships,
  selectById: selectTenantMembershipById,
  selectEntities: selectTenantMembershipEntities,
} = tenantMembershipsAdapter.getSelectors((state) => state.auth.tenantMemberships);

// Selectors
/**
 * Select current user
 * @param {{auth: AuthState}} state
 * @returns {User|null}
 */
export const selectUser = (state) => state.auth.user;

/**
 * `/api/auth/me`'s `pending_rock_setup` (ROCK-ONBOARDING-REWORK T1):
 * `{connection_id, onboarding_state, setup_path}` for an owner or
 * administrator whose linked Rock hasn't finished guided setup, else null.
 * Drives the once-per-session redirect into `/setup/rock/<id>`.
 * @param {{auth: AuthState}} state
 * @returns {{connection_id: string, onboarding_state: string, setup_path: string}|null}
 */
export const selectPendingRockSetup = (state) => {
  const pending = state.auth?.user?.pending_rock_setup;
  return pending && typeof pending === "object" ? pending : null;
};

/**
 * `/api/auth/me`'s `chatgpt_enabled`: ChatGPT account linking is on for this
 * user (globally, or for one of their organizations). Off unless exactly true.
 * @param {{auth: AuthState}} state
 * @returns {boolean}
 */
export const selectIsChatGPTEnabled = (state) => state.auth?.user?.chatgpt_enabled === true;

/**
 * Select current tenant ID
 * @param {{auth: AuthState}} state
 * @returns {string|null}
 */
export const selectCurrentTenantId = (state) => state.auth.currentTenantId;

/**
 * Select current tenant (derives full object from ID)
 * @param {{auth: AuthState}} state
 * @returns {Tenant|null}
 */
export const selectTenant = (state) => {
  const tenantId = state.auth.currentTenantId;
  if (!tenantId) return null;
  return selectTenantById(state, tenantId) || null;
};

/**
 * Select all tenants (for backward compatibility)
 * @param {{auth: AuthState}} state
 * @returns {Array<Tenant>}
 */
export const selectTenants = (state) => selectAllTenantsFromEntities(state);

/**
 * Select authentication status
 * @param {{auth: AuthState}} state
 * @returns {boolean}
 */
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;

/**
 * Select loading state
 * @param {{auth: AuthState}} state
 * @returns {boolean}
 */
export const selectIsLoading = (state) => state.auth.isLoading;
export const selectIsSessionVerified = (state) => state.auth.isSessionVerified;

/**
 * Select error message
 * @param {{auth: AuthState}} state
 * @returns {string|null}
 */
export const selectError = (state) => state.auth.error;

/**
 * Select tenant-aware login context.
 * @param {{auth: AuthState}} state
 * @returns {Object}
 */
export const selectLoginContext = (state) => state.auth.loginContext;

/**
 * Select magic link state
 * @param {{auth: AuthState}} state
 * @returns {Object}
 */
export const selectMagicLink = (state) => state.auth.magicLink;

/**
 * Check if tenant selection is needed
 * User is authenticated with tenants available but no tenant selected
 * This includes users with multiple tenants AND users with zero tenants (who need to see "no tenants" message)
 * @param {{auth: AuthState}} state
 * @returns {boolean}
 */
export const selectNeedsTenantSelection = (state) => {
  const tenantIds = state.auth.tenants.ids;
  return state.auth.isAuthenticated &&
    !state.auth.currentTenantId &&
    (tenantIds.length !== 1) && // Show tenant selector if: 0 tenants (error state) or 2+ tenants (choice needed)
    // On a workspace host the host picks the tenant; the picker waits for it.
    !selectIsHostTenantPending(state);
};

/** Role assumed while impersonating into a tenant with no membership row. */
const IMPERSONATION_FALLBACK_ROLE = 'global_super_admin';

/**
 * Select current user's role for the active tenant
 * @param {{auth: AuthState}} state
 * @returns {string|null} Role like 'administrator', 'owner', 'viewer', etc.
 */
export const selectCurrentUserRole = createSelector(
  [selectCurrentTenantId, selectAllTenantMemberships, selectImpersonation],
  (tenantId, memberships, impersonation) => {
    if (!tenantId) return null;
    const membership = memberships.find(m =>
      m.tenant === tenantId || m.tenant_id === tenantId || m.tenant?.id === tenantId
    );
    if (membership?.role) return membership.role;
    // Impersonating into a tenant the payload holds no membership for: the
    // role the backend pinned, else no UI restrictions (Steven, 86e39gctg).
    // The backend enforces the real rights either way.
    if (impersonation?.active && String(impersonation.tenant.id) === String(tenantId)) {
      return impersonation.role || IMPERSONATION_FALLBACK_ROLE;
    }
    return null;
  }
);

/**
 * Check if current user can manage (create/edit categories, metrics)
 * manager, owner, admin, global_super_admin can create categories/metrics
 * Note: roles are stored lowercase in the database
 * @param {{auth: AuthState}} state
 * @returns {boolean}
 */
export const selectCanManage = createSelector(
  [selectCurrentUserRole],
  (role) => {
    if (!role) return false;
    const normalizedRole = role.toLowerCase();
    return ['administrator', 'owner', 'global_super_admin'].includes(normalizedRole);
  }
);

/**
 * Whether the current user can add or change data (entries, CSV imports).
 *
 * Mirrors the backend's @require_editor, which admits ROLE_EDITOR and above
 * in the hierarchy viewer(1) < editor(2) < administrator(3) < owner(4) <
 * global_super_admin(5). Deliberately wider than selectCanManage, which is
 * administrator and above - an editor can enter data without being able to
 * manage the organisation.
 *
 * Roles are stored lowercase in the database.
 *
 * @param {{auth: AuthState}} state
 * @returns {boolean}
 */
/** Shown on controls a viewer cannot use, so the reason is on the control itself. */
export const EDITOR_ROLE_HINT = 'This needs the Editor role. Ask an administrator to change your access.';

/** Shown on controls that need administrator rather than editor. */
export const ADMIN_ROLE_HINT = 'This needs the Administrator role. Ask an owner to change your access.';

export const selectCanEdit = createSelector(
  [selectCurrentUserRole],
  (role) => {
    if (!role) return false;
    return ['editor', 'administrator', 'owner', 'global_super_admin'].includes(
      role.toLowerCase()
    );
  }
);

/** Shown on controls that need owner rather than administrator. */
export const OWNER_ROLE_HINT =
  'Only the organization owner can change billing. Ask them to make this change.';

/**
 * Whether the current user can change what the organization pays.
 *
 * Mirrors the backend's @require_owner on the Stripe checkout and customer
 * portal endpoints. Narrower than selectCanManage on purpose: an administrator
 * runs the organization but does not open the billing portal, where the
 * subscription can be changed or cancelled.
 *
 * @param {{auth: AuthState}} state
 * @returns {boolean}
 */
export const selectCanManageBilling = createSelector(
  [selectCurrentUserRole],
  (role) => {
    if (!role) return false;
    return ['owner', 'global_super_admin'].includes(role.toLowerCase());
  }
);

export const selectIsGlobalSuperAdmin = createSelector(
  [selectCurrentUserRole],
  (role) => {
    if (!role) return false;
    return role.toLowerCase() === 'global_super_admin';
  }
);

/**
 * Select MFA state
 * @param {{auth: AuthState}} state
 * @returns {Object}
 */
export const selectMFA = (state) => state.auth.mfa;

/**
 * Select whether MFA challenge is required (login pending MFA)
 * @param {{auth: AuthState}} state
 * @returns {boolean}
 */
export const selectMFARequired = (state) => state.auth.mfa.required;

// ---- Pay-during-signup selectors ----

/** @param {{auth: AuthState}} state */
export const selectPendingSignups = (state) => state.auth.pendingSignups;

/** @param {{auth: AuthState}} state */
export const selectPendingSignupsLoaded = (state) => state.auth.pendingSignupsLoaded;

/**
 * Whether the user has at least one active reservation awaiting a plan.
 * @param {{auth: AuthState}} state
 * @returns {boolean}
 */
export const selectHasPendingSignup = (state) => (state.auth.pendingSignups?.length || 0) > 0;

/** @param {{auth: AuthState}} state */
export const selectSignupPlanState = (state) => state.auth.signupPlan;

// ---- Owner self-service reactivation selectors (#187 follow-up) ----

/** @param {{auth: AuthState}} state */
export const selectDeactivatedTenants = (state) => state.auth.deactivatedTenants;

/** @param {{auth: AuthState}} state */
export const selectDeactivatedTenantsLoaded = (state) => state.auth.deactivatedTenantsLoaded;

/** @param {{auth: AuthState}} state */
export const selectReactivatingTenantId = (state) => state.auth.reactivatingTenantId;

/**
 * Whether the authenticated user currently has zero tenants.
 * @param {{auth: AuthState}} state
 * @returns {boolean}
 */
export const selectHasNoTenants = (state) => state.auth.tenants.ids.length === 0;

export default authSlice.reducer;
